import type { jsPDF } from 'jspdf';

/** jsPDF family name for the embedded Unicode / CJK-capable font. */
export const PDF_UNICODE_FONT = 'NotoSansSC';

const FONT_FILE = 'NotoSansSC-Regular.ttf';
/** Served from `frontend/public/fonts/` (CRA public root). */
const FONT_URL = `${process.env.PUBLIC_URL || ''}/fonts/${FONT_FILE}`;

let cachedFontBinary: string | null = null;
let loadingPromise: Promise<string> | null = null;

const arrayBufferToBinaryString = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, Array.from(chunk) as number[]);
  }
  return binary;
};

const loadUnicodeFontBinary = async (): Promise<string> => {
  if (cachedFontBinary) return cachedFontBinary;
  if (loadingPromise) return loadingPromise;

  loadingPromise = (async () => {
    const response = await fetch(FONT_URL);
    if (!response.ok) {
      throw new Error(`Failed to load PDF Unicode font (${response.status})`);
    }
    const binary = arrayBufferToBinaryString(await response.arrayBuffer());
    cachedFontBinary = binary;
    return binary;
  })().finally(() => {
    loadingPromise = null;
  });

  return loadingPromise;
};

/**
 * Registers a CJK-capable font on the jsPDF document and sets it as current.
 * Helvetica cannot render Chinese / other non-Latin glyphs, which shows as
 * illegible symbols — call this before drawing any text that may include them.
 */
export const ensurePdfUnicodeFont = async (doc: jsPDF): Promise<typeof PDF_UNICODE_FONT> => {
  const fontList = doc.getFontList();
  if (!fontList[PDF_UNICODE_FONT]) {
    const binary = await loadUnicodeFontBinary();
    doc.addFileToVFS(FONT_FILE, binary);
    // Same face for all styles so setFont(..., 'bold'|'italic') keeps working
    // without requiring separate bold/italic TTF files.
    doc.addFont(FONT_FILE, PDF_UNICODE_FONT, 'normal');
    doc.addFont(FONT_FILE, PDF_UNICODE_FONT, 'bold');
    doc.addFont(FONT_FILE, PDF_UNICODE_FONT, 'italic');
    doc.addFont(FONT_FILE, PDF_UNICODE_FONT, 'bolditalic');
  }

  doc.setFont(PDF_UNICODE_FONT, 'normal');
  return PDF_UNICODE_FONT;
};
